import {useEffect, useRef} from 'react';
import modestExperimentOverrideStore, {
  modestExperimentOverrideActions,
} from '@/stores/modestExperimentOverrideStore';

// The sequence to trigger the developer override modal
const TRIGGER_SEQUENCE = ['Control', 'Shift', 'M'];
const SEQUENCE_TIMEOUT = 2000; // 2 seconds to complete the sequence

function useModestExperimentOverrideShortcut() {
  const sequenceRef = useRef<string[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Subscribe to store changes to react to isEnabled updates
  const isEnabled = modestExperimentOverrideStore((state) => state.isEnabled);

  useEffect(() => {
    // Don't add event listener if developer overrides are not enabled
    if (!isEnabled) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const {key} = event;

      // Clear sequence if it's been too long
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Add key to sequence
      sequenceRef.current.push(key);

      // Check if sequence matches
      if (sequenceRef.current.length === TRIGGER_SEQUENCE.length) {
        const isMatch = TRIGGER_SEQUENCE.every(
          (expectedKey, index) => sequenceRef.current[index] === expectedKey
        );

        if (isMatch) {
          event.preventDefault();
          modestExperimentOverrideActions.setIsModalOpen(true);
        }

        // Reset sequence
        sequenceRef.current = [];
      } else {
        // Set timeout to reset sequence if not completed
        timeoutRef.current = setTimeout(() => {
          sequenceRef.current = [];
        }, SEQUENCE_TIMEOUT);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isEnabled]); // React to isEnabled changes
}

export default useModestExperimentOverrideShortcut;
